import type { HeldSmsRecord } from '@lib/care-model';

/**
 * THE AFTER-HOURS HOLD ON THE SCREEN (William, 16 Oct 2026; the API half is api/src/lib/sms-held.ts). A held text is
 * NOT sent: its bubble says when it will be tried, in the member's time and in Pacific time, and never "Sent" until
 * the release has really sent it. These words are pinned by tests/unit/sms-hold-screen.test.tsx.
 */

const PT = 'America/Los_Angeles';

function clock(at: Date, zone: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: zone, hour: 'numeric', minute: '2-digit' }).format(at);
  } catch {
    return new Intl.DateTimeFormat('en-US', { timeZone: PT, hour: 'numeric', minute: '2-digit' }).format(at);
  }
}

function weekday(at: Date, zone: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: zone, weekday: 'short' }).format(at);
  } catch {
    return new Intl.DateTimeFormat('en-US', { timeZone: PT, weekday: 'short' }).format(at);
  }
}

/** "Held until 9:05 AM Sat (member time, 8:05 AM PT)". An unreadable time says so rather than guessing. */
export function heldUntilLabel(iso: string | undefined, zone: string | undefined): string {
  const at = new Date(String(iso ?? ''));
  if (Number.isNaN(at.getTime())) return 'Held for the morning';
  const z = zone || PT;
  return `Held until ${clock(at, z)} ${weekday(at, z)} (member time, ${clock(at, PT)} PT)`;
}

/** A finished item stays on screen this many days after its outcome. */
export const HELD_FINISHED_SHOWN_DAYS = 3;

/** Whether a held item is still shown (every unfinished one; a finished one for HELD_FINISHED_SHOWN_DAYS). */
export function heldShown(h: HeldSmsRecord, now: Date = new Date()): boolean {
  if (h.state === 'held' || h.state === 'releasing') return true;
  const at = Date.parse(h.outcome?.at ?? '') || Date.parse(h.heldAt) || 0;
  return now.getTime() - at < HELD_FINISHED_SHOWN_DAYS * 86_400_000;
}

/** The line under a held bubble. Only the 'sent' state ever says Sent. */
export function heldStatusLine(h: HeldSmsRecord): string {
  switch (h.state) {
    case 'held':
      return `${heldUntilLabel(h.sendAfter, h.memberZone)}. Not sent yet: it sends by itself then.`;
    case 'releasing':
      return 'Being sent now. Not confirmed yet.';
    case 'sent':
      return `Sent ${h.outcome?.at ? `at ${clock(new Date(h.outcome.at), PT)} PT` : 'in the morning'}.`;
    case 'not-sent-opted-out':
      return 'NOT SENT. The member opted out before it could be sent.';
    case 'failed':
      return `NOT SENT${h.outcome?.reason ? ` (${h.outcome.reason})` : ''}. Send the reply again by hand; that makes a fresh request.`;
    case 'cancelled':
      return 'Cancelled. Not sent.';
    default:
      return 'NOT SENT.';
  }
}
